import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Centered countdown overlay shown while the player is waiting for a free
 * hint.  The timer ONLY ticks while the browser tab is visible
 * (Page Visibility API) — switching tabs / minimising the window pauses it.
 *
 * On reaching zero we hit /hints/timer-unlock which validates the elapsed
 * time server-side and marks the hint as unlocked.
 */
export default function HintTimerOverlay({ panoramaId, totalSeconds, onClose, onUnlocked, t }) {
  const [remaining, setRemaining] = useState(totalSeconds);
  // Track tab visibility and window focus independently. Switching to
  // another app fires window.blur but does NOT change document.hidden,
  // so we need both signals to detect 'screen attention lost'.
  const [tabHidden, setTabHidden] = useState(typeof document !== 'undefined' && document.hidden);
  const [winBlurred, setWinBlurred] = useState(typeof document !== 'undefined' && !document.hasFocus());

  // Anti-cheat: split the countdown into 4 random chunks. At each of the 3
  // boundaries we pause and require the user to click "I'm still here" to
  // continue. This stops them from popping the tab out, minimising it, and
  // pretending they were still watching the page.
  const [checkpoints, setCheckpoints] = useState(() => {
    if (totalSeconds < 16) return []; // too short to bother
    const minChunk = Math.max(4, Math.floor(totalSeconds / 8));
    const free = totalSeconds - 4 * minChunk;
    if (free < 0) return [];
    const cuts = [Math.random(), Math.random(), Math.random()]
      .map(r => r * free).sort((a, b) => a - b);
    const chunks = [
      minChunk + cuts[0],
      minChunk + (cuts[1] - cuts[0]),
      minChunk + (cuts[2] - cuts[1]),
    ].map(Math.round); // we only need the first three boundaries
    const cps = [];
    let elapsed = 0;
    for (const c of chunks) {
      elapsed += c;
      cps.push(totalSeconds - elapsed); // remaining seconds at this checkpoint
    }
    return cps; // descending order — first hit first
  });
  const [awaitingClick, setAwaitingClick] = useState(false);

  const paused = tabHidden || winBlurred || awaitingClick;
  const [unlocking, setUnlocking] = useState(false);
  const lastTickRef = useRef(Date.now());

  // Tick down 1 second every second only when the tab is visible.
  useEffect(() => {
    if (paused || remaining <= 0) return;
    lastTickRef.current = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      const dt = Math.floor((now - lastTickRef.current) / 1000);
      if (dt >= 1) {
        lastTickRef.current = now;
        setRemaining(r => {
          const next = Math.max(0, r - dt);
          // If we crossed a checkpoint boundary, fire the gate.
          if (checkpoints.length > 0 && next > 0 && next <= checkpoints[0]) {
            setAwaitingClick(true);
            setCheckpoints(arr => arr.slice(1));
          }
          return next;
        });
      }
    }, 250);
    return () => clearInterval(id);
  }, [paused, remaining]);

  // Pause on (a) tab hidden  (b) window blurred (e.g. user switched to a
  // different desktop app). Re-anchor the tick clock when we resume so the
  // background time is not double-counted.
  useEffect(() => {
    const onTabVisChange = () => setTabHidden(document.hidden);
    const onWindowBlur = () => setWinBlurred(true);
    const onWindowFocus = () => {
      lastTickRef.current = Date.now();
      setWinBlurred(false);
    };
    document.addEventListener('visibilitychange', onTabVisChange);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', onTabVisChange);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, []);

  // When the tab becomes visible again, re-anchor the tick clock too.
  useEffect(() => {
    if (!tabHidden) lastTickRef.current = Date.now();
  }, [tabHidden]);

  // When the local counter hits 0, ask the server to unlock.
  useEffect(() => {
    if (remaining > 0 || unlocking) return;
    setUnlocking(true);
    axios.post(`${API}/api/hints/timer-unlock/${panoramaId}`, {}, { headers: getAuthHeader() })
      .then(() => {
        toast.success(t('guessGame.hintUnlocked') || 'Подсказка открыта!');
        onUnlocked();
      })
      .catch(e => {
        const detail = e.response?.data?.detail || '';
        // If server says the timer hasn't finished (network drift), give it a
        // couple more seconds and retry once.
        const m = detail.match(/(\d+)s left/);
        if (m) {
          const extra = Math.min(10, parseInt(m[1], 10) + 2);
          setTimeout(() => {
            axios.post(`${API}/api/hints/timer-unlock/${panoramaId}`, {}, { headers: getAuthHeader() })
              .then(() => { toast.success(t('guessGame.hintUnlocked') || 'Подсказка открыта!'); onUnlocked(); })
              .catch(() => { toast.error(detail); setUnlocking(false); });
          }, extra * 1000);
        } else {
          toast.error(detail || 'Не удалось активировать подсказку');
          setUnlocking(false);
        }
      });
  }, [remaining, unlocking, panoramaId, onUnlocked, t]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div
      data-testid="hint-timer-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f7ff 100%)',
        borderRadius: 24, padding: '32px 28px', maxWidth: 420, width: '100%',
        textAlign: 'center', boxShadow: '0 40px 100px rgba(10,71,151,0.4)',
        border: '1px solid #d1d9ee',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', margin: '0 auto 14px',
          background: 'linear-gradient(135deg, #0a4797 0%, #1e5fb8 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white',
        }}>
          <Clock size={32} />
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0a4797', marginBottom: 6 }}>
          {t('guessGame.freeHintTimerTitle') || 'Подсказка скоро откроется'}
        </h3>
        <p style={{ fontSize: 13.5, color: '#475569', marginBottom: 22, lineHeight: 1.5 }}>
          {t('guessGame.freeHintTimerHint') ||
            'Оставайтесь на этой странице. Если вы переключитесь на другую вкладку, таймер встанет на паузу.'}
        </p>

        {awaitingClick ? (
          <div data-testid="hint-timer-checkpoint" style={{ margin: '8px 0 14px 0' }}>
            <div style={{
              fontSize: 14, color: '#475569', marginBottom: 14, lineHeight: 1.5,
            }}>
              {t('guessGame.freeHintTimerCheckpoint') || 'Вы ещё здесь? Нажмите, чтобы продолжить обратный отсчёт'}
            </div>
            <button
              type="button"
              data-testid="hint-timer-checkpoint-btn"
              onClick={() => {
                lastTickRef.current = Date.now();
                setAwaitingClick(false);
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '100%', padding: '20px 22px', borderRadius: 16,
                border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #0a4797 0%, #1e5fb8 100%)',
                color: 'white', fontSize: 22, fontWeight: 700, lineHeight: 1,
                boxShadow: '0 14px 36px rgba(10,71,151,0.36)',
                transition: 'transform 0.15s ease, box-shadow 0.2s ease',
                animation: 'hint-timer-pulse-btn 1.4s ease-in-out infinite',
              }}
            >
              {t('guessGame.freeHintTimerContinue') || 'Продолжить'}
            </button>
            <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
              {mm}:{ss}
            </div>
          </div>
        ) : (
          <div
            data-testid="hint-timer-display"
            style={{
              fontSize: 'clamp(64px, 18vw, 96px)', fontWeight: 800, fontFeatureSettings: '"tnum" 1',
              color: paused ? '#94a3b8' : '#0a4797', lineHeight: 1,
              letterSpacing: '0.04em', marginBottom: 12,
              transition: 'color 0.2s ease',
            }}
          >
            {mm}:{ss}
          </div>
        )}

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', borderRadius: 999,
          background: paused ? '#fef3c7' : '#dcfce7',
          color: paused ? '#92400e' : '#166534',
          fontSize: 12.5, fontWeight: 600, marginBottom: 22,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: paused ? '#f59e0b' : '#22c55e',
            animation: paused ? 'none' : 'hint-timer-pulse 1.4s ease-in-out infinite',
          }} />
          {paused
            ? (t('guessGame.freeHintTimerPaused') || 'На паузе (вкладка не активна)')
            : (t('guessGame.freeHintTimerRunning') || 'Идёт обратный отсчёт')}
        </div>

        <div>
          <button
            type="button"
            onClick={onClose}
            data-testid="hint-timer-cancel"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 22px', borderRadius: 10,
              border: '1px solid #cbd5e1', background: 'white',
              color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <X size={16} /> {t('guessGame.cancel') || 'Отмена'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes hint-timer-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes hint-timer-pulse-btn {
          0%, 100% { transform: scale(1); box-shadow: 0 14px 36px rgba(10,71,151,0.36); }
          50%      { transform: scale(1.03); box-shadow: 0 20px 50px rgba(10,71,151,0.55); }
        }
      `}</style>
    </div>
  );
}
